import { type AnyRouter, rootRouteId } from "@tanstack/react-router"

import { type ScreenViewPlatform, trackScreenView } from "@/api/others/analytics"
import { useAuthStore } from "@/modules/helpers/hooks/shared/use-auth"
import { useShowAffiliate } from "@/modules/helpers/hooks/shared/use-show-affiliate"
import { useShowCampaign } from "@/modules/helpers/hooks/shared/use-show-campaign"
import { useShowSubmission } from "@/modules/helpers/hooks/shared/use-show-submission"

// Screen views feed DAU + ad-impression (CPM) counts on the backend
// (`koc_app_screen_view_days`). One call = one screen open; the backend keys
// visitors by user when a token is present, otherwise by deviceId.

const DEVICE_ID_STORAGE_KEY = "zzp_device_id"
const DEVICE_ID_PATTERN = /^[A-Za-z0-9._:-]{1,100}$/
// Native answers the first-load token request (sent after ~1s) well within this.
export const TOKEN_WAIT_MS = 5000
// Returning to the app after this long (or on a new VN day) is a new visit.
export const RESUME_VISIT_MS = 30 * 60 * 1000
// Asia/Ho_Chi_Minh is UTC+7 all year (no DST), matching the backend's stat_date.
const VN_OFFSET_MS = 7 * 60 * 60 * 1000
// `_app` renders only a loading spinner until a token exists.
const AUTH_LAYOUT_ROUTE_ID = "/_app"

const trackingFlag = import.meta.env.VITE_APP_SCREEN_TRACKING
export const isScreenTrackingEnabled = trackingFlag ? trackingFlag === "true" : import.meta.env.PROD

export type ScreenView = {
  screen: string
  /** Skip when logged out: the screen is not actually shown without a token. */
  requiresAuth: boolean
}

/** `/campaign/affiliate/$id` → `campaign/affiliate/:id`; the index route is `home`. */
export const toScreenKey = (routeFullPath: string): string =>
  routeFullPath
    .split("/")
    .filter(Boolean)
    .map((segment) => (segment.startsWith("$") ? `:${segment.slice(1) || "splat"}` : segment))
    .join("/")
    .toLowerCase() || "home"

/** Uses the matched route pattern, never the concrete URL, so IDs don't split screens. */
export const getCurrentScreenView = (router: AnyRouter): ScreenView => {
  const { matches } = router.state
  const leaf = matches[matches.length - 1]
  if (
    !leaf ||
    leaf.routeId === rootRouteId ||
    matches.some((match) => match.status === "notFound" || match.globalNotFound)
  ) {
    return { screen: "not_found", requiresAuth: false }
  }
  return {
    screen: toScreenKey(leaf.fullPath),
    requiresAuth: matches.some((match) => match.routeId === AUTH_LAYOUT_ROUTE_ID)
  }
}

// Detail sheets open from store state, not routes, but are full screens to the
// user. All of them live in the `_app` layout.
const SHEET_STORES = [
  { store: useShowCampaign, screen: "sheet/campaign_detail" },
  { store: useShowSubmission, screen: "sheet/submission_detail" },
  { store: useShowAffiliate, screen: "sheet/affiliate_detail" }
] as const

export const toVnDay = (timestamp: number): string =>
  new Date(timestamp + VN_OFFSET_MS).toISOString().slice(0, 10)

const createId = (): string =>
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`

let memoryDeviceId: string | null = null

export const getDeviceId = (): string => {
  try {
    const stored = localStorage.getItem(DEVICE_ID_STORAGE_KEY)
    if (stored && DEVICE_ID_PATTERN.test(stored)) return stored

    const id = createId()
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, id)
    return id
  } catch {
    memoryDeviceId ??= createId()
    return memoryDeviceId
  }
}

export const detectPlatform = (): ScreenViewPlatform => {
  if (!window.ReactNativeWebView) return "WEB"
  const ua = navigator.userAgent
  if (/android/i.test(ua)) return "ANDROID"
  if (/iphone|ipad|ipod/i.test(ua) || (/macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) {
    return "IOS"
  }
  return "WEB"
}

/**
 * Inside the native app the token arrives by postMessage shortly after first
 * load. Holding the first views until then keeps one KOC from being counted
 * once as an anonymous device and again as a user on the same day.
 */
export const waitForIdentity = (timeoutMs = TOKEN_WAIT_MS): Promise<void> => {
  if (useAuthStore.getState().accessToken || !window.ReactNativeWebView) {
    return Promise.resolve()
  }

  return new Promise((resolve) => {
    const done = () => {
      clearTimeout(timer)
      unsubscribe()
      resolve()
    }
    const timer = setTimeout(done, timeoutMs)
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state.accessToken) done()
    })
  })
}

type ScreenTrackerOptions = {
  send: (view: ScreenView) => Promise<unknown>
  identityReady: Promise<void>
  isVisible: () => boolean
  now?: () => number
}

export const createScreenTracker = ({
  send,
  identityReady,
  isVisible,
  now = Date.now
}: ScreenTrackerOptions) => {
  let lastPathname: string | null = null
  let currentView: ScreenView | null = null
  let pendingView: ScreenView | null = null
  let lastSentAt = 0
  let hiddenAt: number | null = null

  const dispatch = (view: ScreenView) => {
    lastSentAt = now()
    void identityReady.then(() => send(view)).catch(() => undefined)
  }

  // A screen opened while the web-view is hidden (preloaded or in the
  // background) is only counted once the user actually sees it.
  const show = (view: ScreenView) => {
    if (isVisible()) {
      pendingView = null
      dispatch(view)
    } else {
      pendingView = view
    }
  }

  return {
    /** Call once per resolved navigation. Search/hash-only changes are not new screens. */
    onNavigate(pathname: string, view: ScreenView) {
      if (pathname === lastPathname) return
      lastPathname = pathname
      currentView = view
      show(view)
    },

    /** A screen that is not a route (sheet, modal) was opened on top of the page. */
    onOverlayOpen(view: ScreenView) {
      show(view)
    },

    onVisibilityChange(visible: boolean) {
      if (!visible) {
        hiddenAt = now()
        return
      }

      const awayFor = hiddenAt === null ? 0 : now() - hiddenAt
      hiddenAt = null

      if (pendingView) {
        const view = pendingView
        pendingView = null
        dispatch(view)
        return
      }

      if (
        currentView &&
        (awayFor >= RESUME_VISIT_MS || (awayFor > 0 && toVnDay(now()) !== toVnDay(lastSentAt)))
      ) {
        dispatch(currentView)
      }
    }
  }
}

/** Subscribes to router navigations and detail sheets; call once after `createRouter`. */
export const startScreenTracking = (router: AnyRouter): (() => void) => {
  if (!isScreenTrackingEnabled || typeof window === "undefined") return () => undefined

  const platform = detectPlatform()
  const appVersion = (import.meta.env.VITE_APP_VERSION as string | undefined)?.slice(0, 50)

  const tracker = createScreenTracker({
    identityReady: waitForIdentity(),
    isVisible: () => document.visibilityState !== "hidden",
    send: ({ screen, requiresAuth }) =>
      requiresAuth && !useAuthStore.getState().accessToken
        ? Promise.resolve()
        : trackScreenView({ screen, deviceId: getDeviceId(), platform, appVersion })
  })

  const unsubscribers = [
    router.subscribe("onResolved", (event) => {
      tracker.onNavigate(event.toLocation.pathname, getCurrentScreenView(router))
    }),
    ...SHEET_STORES.map(({ store, screen }) =>
      store.subscribe((state, previous) => {
        if (state.id && state.id !== previous.id) {
          tracker.onOverlayOpen({ screen, requiresAuth: true })
        }
      })
    )
  ]
  const handleVisibility = () => tracker.onVisibilityChange(document.visibilityState !== "hidden")
  document.addEventListener("visibilitychange", handleVisibility)

  return () => {
    unsubscribers.forEach((unsubscribe) => unsubscribe())
    document.removeEventListener("visibilitychange", handleVisibility)
  }
}
